import type { LucideIcon } from 'lucide-react'
import {
  Calendar,
  CreditCard,
  Filter,
  Headphones,
  MessageSquare,
  Plus,
  ShoppingBag,
  User,
  UserCheck,
} from 'lucide-react'

export interface VibeAgentPromptData {
  icon: LucideIcon
  title: string
  prompt: string
  description: string
  iconColor: string
}

export const VIBE_AGENT_PROMPTS: VibeAgentPromptData[] = [
  {
    icon: Filter,
    title: 'Lead qualification',
    description: 'Qualifies potential customers and connects them to sales',
    prompt: `Create a lead qualification voice call agent that assesses potential customers and sends the collected data to an API endpoint. This flow is triggered via HTTP request with payload containing caller information (to/from numbers, customer name, lead source).

Start with a friendly greeting and ask about their interest in your products/services.

Gather qualification information:
- Contact details (name, email, phone, company)
- Current situation and pain points
- Budget range and decision-making timeline
- Specific needs and requirements
- Estimated business volume
- Company size and role/authority level
- Previous solutions they've tried

Qualify leads using BANT criteria:
- Budget: Can they afford your solution?
- Authority: Are they the decision maker?
- Need: Do they have a clear requirement?
- Timeline: When do they plan to make a decision?

After collecting all information, send the lead data to the API endpoint example.com/leads via a POST request with JSON payload containing:
- All collected contact and qualification information
- Qualification score based on BANT criteria
- Lead status (qualified/unqualified)
- Timestamp of collection

Be professional, consultative, and focused on understanding their needs.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: Calendar,
    title: 'Appointment booking',
    description: 'Automates scheduling and booking requests',
    prompt: `Create an appointment booking voice call agent that helps customers schedule appointments. This flow is triggered on incoming calls from customers looking to book appointments.


Handle booking requests:
- Check availability for requested date/time
- Collect customer details (name, phone, email)
- Confirm appointment details
- Provide clear booking instructions

For complex scheduling issues or special requests, offer to transfer to a human agent.

Be professional, organized, and confirm all details before finalizing bookings.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: Headphones,
    title: 'Customer support',
    description: 'Provides comprehensive and responsive customer support',
    prompt: `Create a versatile help desk voice call assistant for general customer support across various industries. This flow is triggered on incoming calls from customers.

Start with a friendly greeting and identify the customer's issue or question.

Handle common inquiries:
- General product/service information
- Basic troubleshooting and FAQs
- Hours, locations, and contact information

Provide assistance:
- Step-by-step guidance for common tasks
- Clear explanations of policies and procedures
- Resource recommendations and self-service options

For complex issues, technical problems, or specialized requests, politely offer to transfer to appropriate department specialists.

Be helpful, patient, and knowledgeable. Ask clarifying questions when needed and confirm understanding. End calls by ensuring customer satisfaction and offering additional help.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: User,
    title: 'Receptionist',
    description: 'Manages bookings and initial inquiries',
    prompt: `This AI assistant works as the virtual concierge for Aurora Wellness Studio, a premium yoga and pilates center.
Its primary goal is to help visitors:
• Book trial classes or memberships
• Modify or cancel existing class bookings
• Get information about programs, pricing, and instructors
The assistant should collect:
Full name
Type of class they're interested in (e.g. Hatha Yoga, Reform Pilates, Breathwork)
Preferred time slot / day
Experience level (Beginner, Intermediate, Advanced)
Contact details (email and phone number)
If the user is a first-time visitor, ask how they discovered the studio and whether they have any prior injuries or physical limitations.
Once all the details are collected, confirm their class booking or inform them that a studio manager will reach out shortly to finalize availability.
If the user isn't ready to book, offer to share the studio's class schedule, pricing plan, or a beginner starter guide via email, and ask for their preferred contact info.
The assistant should speak in a calm, encouraging, and uplifting tone, like a knowledgeable wellness coach.
It should make the user feel relaxed, supported, and confident about starting their fitness journey.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: ShoppingBag,
    title: 'Ecommerce order tracking',
    description: 'Handles order status and delivery confirmation',
    prompt: `Create a Shopify order tracking voice call agent for e-commerce stores. This flow is triggered on incoming calls from customers checking their order status.

Start with a friendly greeting and ask for their order number or email to look up their order.

Handle order tracking inquiries:
- Provide current order status (processing, shipped, delivered)
- Share tracking numbers and carrier information
- Give estimated delivery dates and times
- Confirm delivery addresses
- Send delivery confirmation details via SMS

Use relevant actions to get shopify order details.

For issues like delayed deliveries, wrong addresses, or missing packages, politely offer to transfer to customer service.

Be helpful, accurate, and reassuring. Always confirm the customer's satisfaction before ending the call.

Use a friendly, professional voice that's warm and approachable.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: MessageSquare,
    title: 'Feedback and survey',
    description: 'Collects feedback via outbound calls',
    prompt: `Create a customer feedback and survey voice call agent that makes outbound calls to gather customer feedback. This flow is triggered via HTTP request with payload containing customer information (to/from numbers, product name, order date, customer name).

Start with a friendly greeting using the customer's name and reference their recent order.

Conduct the feedback survey:
- Ask about overall satisfaction with the product/service
- Gather specific feedback on product quality, delivery, and customer service
- Collect ratings on a scale of 1-5 for different aspects
- Ask open-ended questions about what they liked and what could be improved
- Inquire about likelihood to recommend and repurchase

After collecting all feedback, thank them for their time and send the collected data to your feedback endpoint via a POST request with JSON payload containing:
- All customer information (name, contact details, order info)
- All survey responses and ratings

Be professional, engaging, and appreciative. Keep the survey concise but thorough.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: CreditCard,
    title: 'Payment reminder',
    description: 'Handles payment reminders and collection scheduling',
    prompt: `Create a payment reminder voice call agent for debt collection and payment scheduling. This flow is triggered via HTTP request with payload containing customer information (customer name, to/from numbers, debt amount, due date).

Start with a professional greeting using the customer's name and politely inform them about their outstanding payment.

Notify the customer about:
- Current debt amount and due date
- Any applicable late fees or interest
- Available payment options and methods
- Consequences of non-payment

Ask for a tentative payment date and gather payment preferences:
- Proposed payment date
- Preferred payment method (online, bank transfer, etc.)
- Payment amount breakdown if partial payments are offered
- Contact information confirmation

After collecting all information, send the comprehensive payment data to an API endpoint via a POST request with JSON payload containing:
- Customer information (name, contact details)
- Debt details (amount, due date, outstanding balance)
- Payment commitment (tentative date, amount, method)
- Interaction timestamp and agent notes
- Follow-up scheduling information

Be professional, understanding, and persistent while maintaining compliance with debt collection regulations.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: UserCheck,
    title: 'Interview & Hire',
    description: 'Screens and shortlists job candidates',
    prompt: `This AI agent acts as the first point of contact for job applicants at [COMPANY_NAME].
Its primary goal is to help screen candidates applying for the [JOB_ROLE] role.
The agent should:
Confirm the candidate's name
Ask 2–3 basic eligibility questions (like experience, availability, or required documents)
Collect availability for a follow-up interview
Capture contact details if missing
If the candidate meets the requirements, inform them that they've been shortlisted and someone from HR will reach out shortly.
If they don't qualify, politely thank them and close the conversation respectfully.
If the candidate isn't ready to continue, offer to schedule a callback and ask for a preferred time.
The tone should be friendly, professional, and reassuring — like a warm but efficient HR receptionist.
Focus on making candidates feel respected, even if they're not selected.`,
    iconColor: 'text-brand-text',
  },
  {
    icon: Plus,
    title: 'Start from Scratch',
    description: 'Create your custom AI Agent.',
    prompt:
      'I want to create a custom AI voice call agent from scratch. The flow can be triggered either on incoming calls or via HTTP requests with payload containing caller information (to/from numbers, customer name). Please help me build an agentic flow that can handle my specific business requirements.',
    iconColor: 'text-brand-text',
  },
]

// Every prompt that is a real template — everything except the "Start from
// Scratch" blank-slate entry. The canonical subset for template pickers.
export const VIBE_AGENT_TEMPLATE_PROMPTS: VibeAgentPromptData[] = VIBE_AGENT_PROMPTS.filter(
  (p) => p.title !== 'Start from Scratch',
)
